<template>
  <Teleport to="body">
    <div
      v-if="isVisible"
      class="modal-overlay"
      @click="handleBackdropClick"
    >
      <div
        ref="modalRef"
        class="modal-container"
        :class="{ 'modal-container-medium': size === 'medium' }"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="title ? 'modal-title' : void 0"
        @click.stop
      >
        <div
          v-if="title || showCloseButton"
          class="modal-header"
        >
          <h2
            v-if="title"
            id="modal-title"
            class="modal-title"
          >
            {{ title }}
          </h2>
          <button
            v-if="showCloseButton"
            type="button"
            class="modal-close-button"
            aria-label="Close modal"
            @click="close"
          >
            <Icon
              name="lucide:x"
              size="20"
            />
          </button>
        </div>

        <div class="modal-content">
          <slot />
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from "vue";
import { useVModel, useScrollLock } from "@vueuse/core";
const props = defineProps({
  modelValue: { type: Boolean, required: false, default: false },
  title: { type: String, required: false, default: "" },
  showCloseButton: { type: Boolean, required: false, default: true },
  closeOnBackdrop: { type: Boolean, required: false, default: true },
  closeOnEscape: { type: Boolean, required: false, default: true },
  size: { type: String, required: false, default: "large" }
});
const emit = defineEmits(["update:modelValue", "close", "open"]);
const modalRef = ref(null);
const isVisible = useVModel(props, "modelValue", emit);
const isLocked = useScrollLock(document.body);
watch(isVisible, (visible) => {
  isLocked.value = visible;
}, { immediate: true });
onMounted(() => {
  document.addEventListener("keydown", handleEscapeKey);
});
onUnmounted(() => {
  document.removeEventListener("keydown", handleEscapeKey);
  isLocked.value = false;
});
function handleEscapeKey(event) {
  if (event.key === "Escape" && props.closeOnEscape && isVisible.value) {
    close();
  }
}
function handleBackdropClick(event) {
  if (!props.closeOnBackdrop) return;
  const target = event.target;
  if (modalRef.value && !modalRef.value.contains(target)) {
    close();
  }
}
function close() {
  isVisible.value = false;
  emit("close");
}
function open() {
  isVisible.value = true;
  emit("open");
}
defineExpose({
  open,
  close
});
</script>

<style scoped>
.modal-overlay{align-items:center;background-color:rgba(0,0,0,.5);bottom:0;display:flex;justify-content:center;left:0;position:fixed;right:0;top:0;z-index:1000}.modal-container{--color-grey-950:#2d2d2d;--color-black:#000;--space-600:24px;--border-radius-xs:4px;background:#fff;border-radius:var(--border-radius-xs);box-shadow:0 1px 14px -1px rgba(0,0,0,.14),0 0 5px 0 rgba(0,0,0,.12);margin:20px;max-height:90vh;max-width:750px;outline:none;overflow:hidden;position:relative;width:100%;z-index:1001}.modal-container.modal-container-medium{max-width:571px}.modal-header{align-items:center;display:flex;justify-content:space-between;padding:var(--space-600) var(--space-600) 0 var(--space-600)}.modal-title{color:var(--color-grey-950);font-family:Inter;font-size:24px;font-weight:800;line-height:24px;margin:0}.modal-close-button{background-color:transparent;border:none;color:var(--color-black);cursor:pointer;height:20px;padding:0}.modal-close-button:hover{background-color:transparent;background:transparent}.modal-close-button:focus{outline:2px solid #3b82f6;outline-offset:2px}.modal-content{max-height:calc(90vh - 120px);overflow-y:auto;padding:0 var(--space-600) var(--space-600) var(--space-600)}.modal-overlay{animation:fadeIn .2s ease-out}.modal-container{animation:slideIn .2s ease-out}@keyframes fadeIn{0%{opacity:0}to{opacity:1}}@keyframes slideIn{0%{opacity:0;transform:translateY(-20px) scale(.95)}to{opacity:1;transform:translateY(0) scale(1)}}@media (max-width:640px){.modal-container{margin:16px;max-width:95vw}}
</style>
